import { UserFormData } from "@repo/types";
import { LockIcon, MailIcon, UserIcon } from "lucide-react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../components/ui/form";
import { Input } from "../../../../components/ui/input";
import { PasswordRules } from "./password-rules";
import { ValidationRule } from "./validation-rule";

type FormValues = UserFormData & { confirmPassword?: string };

interface UserBasicSectionProps {
  form: UseFormReturn<FormValues>;
  isEditMode: boolean;
  fieldErrors: Record<string, string>;
}

export function UserBasicSection({
  form,
  isEditMode,
  fieldErrors,
}: UserBasicSectionProps) {
  const password = useWatch({ control: form.control, name: "password" }) ?? "";
  const confirmPassword = useWatch({
    control: form.control,
    name: "confirmPassword",
  });
  const username = useWatch({ control: form.control, name: "username" }) ?? "";

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserIcon className="h-5 w-5 text-primary" />
          Informations de base
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  Nom d'utilisateur
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Nom d'utilisateur"
                    {...field}
                    value={field.value || ""}
                    className={
                      fieldErrors.username ? "border-destructive focus:border-destructive" : ""
                    }
                  />
                </FormControl>
                <FormMessage />
                <div className="mt-2 p-3 bg-muted/40 border border-border rounded-md">
                  <p className="text-xs font-medium mb-1.5">Règles :</p>
                  <div className="space-y-1">
                    <ValidationRule
                      isValid={!!username && username.length >= 1}
                      label="Au moins 1 caractère (requis)"
                    />
                    <ValidationRule
                      isValid={!username || username.length <= 255}
                      label="Maximum 255 caractères"
                    />
                  </div>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium flex items-center gap-2">
                  <MailIcon className="h-4 w-4 text-muted-foreground" />
                  Adresse email
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="email@exemple.com"
                    type="email"
                    {...field}
                    value={field.value || ""}
                    className={
                      fieldErrors.email ? "border-destructive focus:border-destructive" : ""
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="border-t pt-6">
          <h4 className="flex items-center gap-2 text-sm font-medium mb-4">
            <LockIcon className="h-4 w-4 text-muted-foreground" />
            Mot de passe
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    {isEditMode ? "Nouveau mot de passe" : "Mot de passe"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        isEditMode ? "Laisser vide pour ne pas changer" : "Mot de passe"
                      }
                      type="password"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                  <PasswordRules
                    password={password}
                    confirmPassword={confirmPassword}
                    isEditMode={isEditMode}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">Confirmer le mot de passe</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Confirmer le mot de passe"
                      type="password"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
